import { differenceInDays, isBefore } from 'date-fns';
import { useLogout, useUserHadTrial } from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  CommandButton16,
  Discord16,
  Dot,
  ExitLogOutArrow16,
  GiftBox16,
  LifeAreaRocket,
  MessageChatModern16,
  Reddit16,
  SettingsRound16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  changelogLink,
  discordLink,
  redditLink,
  shortcutsLink,
} from 'shared/constants';
import { useOpenFeedbackDialog } from 'shared/contexts/feedback-dialog';
import { useOpenPremiumDialogWithPriceSelection } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useOpenSettings } from 'shared/contexts/user-settings';
import { useNewVersionAvailable } from 'shared/contexts/version';
import { useUser } from 'shared/hooks/use-user';
import { Entitlements } from 'shared/types/entitlements';
import { PriceInterval } from 'shared/types/price';

import * as Styled from './user-context-menu.style';

export type UserContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref' | 'location' | 'onOutside'
> & {
  onClose: () => void;
};

export const UserContextMenu: React.FC<UserContextMenuProps> = ({
  onClose,
  referenceElement,
  position = 'bottom-start',
}) => {
  const { t } = useTranslation();
  const today = useToday();

  const user = useUser();
  const userHadTrial = useUserHadTrial();
  const newVersionAvailable = useNewVersionAvailable();

  const openPremiumDialog = useOpenPremiumDialogWithPriceSelection();
  const openFeedbackDialog = useOpenFeedbackDialog();
  const logout = useLogout();
  const openSettings = useOpenSettings();

  const userIsPremium = useMemo(
    () => user?.entitlements?.includes(Entitlements.Premium),
    [user?.entitlements],
  );

  const activeLifetimeTrial = useMemo(() => {
    const activeSubscription = user?.subscriptions
      // filter out the expired subscriptions
      ?.filter(({ expiresAt }) => !expiresAt || isBefore(today, expiresAt))
      // sort them from to have the latest purchase first
      ?.sort(
        ({ purchasedAt: purchasedAtA }, { purchasedAt: purchasedAtB }) =>
          new Date(purchasedAtB).getTime() - new Date(purchasedAtA).getTime(),
      )[0];
    return activeSubscription?.productId?.includes('custom')
      ? activeSubscription
      : undefined;
  }, [today, user?.subscriptions]);

  const onSettings = () => {
    openSettings();
    onClose();
  };

  const onFeedback = () => {
    openFeedbackDialog();
    onClose();
  };

  // @ts-ignore -- reload(true) should forceGet: https://developer.mozilla.org/en-US/docs/Web/API/Location/reload#forceget
  const onReload = () => window.location.reload(true);

  const onLogout = () => {
    logout();
    onClose();
  };

  const onShortcutsLink = () => {
    window.open(shortcutsLink, '_blank');
    onClose();
  };

  const onRedditLink = () => {
    window.open(redditLink, '_blank');
    onClose();
  };

  const onDiscordLink = () => {
    window.open(discordLink, '_blank');
    onClose();
  };

  const onChangelogLink = () => {
    window.open(changelogLink, '_blank');
    onClose();
  };

  const onIAP = () => {
    openPremiumDialog(activeLifetimeTrial ? PriceInterval.Lifetime : undefined);
    onClose();
  };

  return (
    <PopupMenu
      referenceElement={referenceElement}
      position={position}
      onOutside={onClose}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onSettings}
            start={<Icon icon={SettingsRound16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.settings')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onShortcutsLink}
            start={<Icon icon={CommandButton16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.keyboard-shortcuts')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      {newVersionAvailable && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onReload}
              start={<Icon icon={ArrowDown} />}
              end={<Icon icon={Dot} />}
              endColor={Colors.Error}
              tabIndex={0}
            >
              {t('context-menus.user.labels.reload')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {(!userIsPremium || !!activeLifetimeTrial) && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onIAP}
              start={<Icon icon={LifeAreaRocket} />}
              startColor={Colors.Warning}
              tabIndex={0}
            >
              <Styled.PremiumLabelContainer>
                {t(
                  activeLifetimeTrial
                    ? 'context-menus.user.labels.purchase-premium'
                    : userHadTrial
                      ? 'context-menus.user.labels.upgrade-premium'
                      : 'context-menus.user.labels.try-premium-free',
                )}
                {!!activeLifetimeTrial && !!activeLifetimeTrial.expiresAt && (
                  <Styled.LifetimeTrialLabel>
                    {t('context-menus.user.labels.trial-time-left', {
                      count: differenceInDays(
                        activeLifetimeTrial.expiresAt,
                        today,
                      ),
                    })}
                  </Styled.LifetimeTrialLabel>
                )}
              </Styled.PremiumLabelContainer>
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onDiscordLink}
            start={<Icon icon={Discord16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.discord-community')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onRedditLink}
            start={<Icon icon={Reddit16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.reddit-community')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onFeedback}
            start={<Icon icon={MessageChatModern16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.share-feedback')}
          </PopupMenuButton>
        </PopupMenuListItem>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onChangelogLink}
            start={<Icon icon={GiftBox16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.whats-new')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        <PopupMenuListItem>
          <PopupMenuButton
            onClick={onLogout}
            start={<Icon icon={ExitLogOutArrow16} />}
            tabIndex={0}
          >
            {t('context-menus.user.labels.logout')}
          </PopupMenuButton>
        </PopupMenuListItem>
      </PopupMenuList>
    </PopupMenu>
  );
};
